<template>
  <div v-if="featureFlagEnabled">
    <div class="min-h-integrationCard flex flex-col justify-between shadow-appComponent border-signableBrandColours-midGrey border-solid border-1 rounded-xl p-base">
      <div class="integration-overview">
        <div
          v-if="!isLoading"
          :class="hasIntegration ? 'bg-signableGreen text-black' : 'bg-primary text-white'"
          class="text-xs float-right px-sm py-xs rounded-full">
          {{ hasIntegration ? locale.connected : locale.disconnected }}
        </div>
        <img
          src="/img/integrations/slack.svg"
          width="50"
          height="50"
          class="mb-base w-fifty h-fifty"
          alt=""
        />
        <h2>
          {{ locale.name }}
        </h2>
        <p>
          {{ locale.headline }}
        </p>
      </div>
      <div>
        <BaseButton
          v-if="!hasIntegration"
          @click.native="createIntegration"
          :disabled="isProcessing"
        >
          {{ buttonText }}
        </BaseButton>
        <ButtonLink
          v-else
          :to="configurationUrl"
          :disabled="isProcessing"
        >
          {{ isProcessing ? locale.loading : locale.manage }}
        </ButtonLink>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref } from 'vue';
import { useListIntegrations, useFeatureFlag, useGenerateAuthUrl } from '@/composables/integrations/slack';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import ButtonLink from '@/components/library/links/ButtonLink.vue';
import { delaySeconds } from '@/utils/delay';
import { t } from '@/locales';

export default defineComponent({
  name: 'SlackCardScreen',
  components: {
    BaseButton,
    ButtonLink,
  },
  setup() {
    const { isEnabled: featureFlagEnabled } = useFeatureFlag();

    const {
      isLoading,
      hasResults,
      results,
      fetch,
    } = useListIntegrations();

    const {
      isGenerating,
      generate,
    } = useGenerateAuthUrl();

    // eslint-disable-next-line arrow-body-style
    const isProcessing = computed(() => {
      return isLoading.value || isGenerating.value;
    });

    // eslint-disable-next-line arrow-body-style
    const integration = computed(() => {
      return hasResults.value ? results.value[0] : null;
    });

    // eslint-disable-next-line arrow-body-style
    const hasIntegration = computed(() => {
      return integration.value !== null;
    });

    // eslint-disable-next-line arrow-body-style
    const configurationUrl = computed(() => {
      return integration.value?.configuration_url;
    });

    const locale = {
      name: t('integrations.slack.screens.slackCardScreen.name'),
      headline: t('integrations.slack.screens.slackCardScreen.headline'),
      connected: t('integrations.slack.screens.slackCardScreen.connected'),
      disconnected: t('integrations.slack.screens.slackCardScreen.disconnected'),
      loading: t('integrations.slack.screens.slackCardScreen.loading'),
      createNew: t('integrations.slack.screens.slackCardScreen.createNew'),
      generatingUrl: t('integrations.slack.screens.slackCardScreen.generatingUrl'),
      redirecting: t('integrations.slack.screens.slackCardScreen.redirecting'),
      manage: t('integrations.slack.screens.slackCardScreen.manage'),
      error: t('integrations.slack.screens.slackCardScreen.error'),
    };

    const buttonText = ref(locale.loading);

    async function createIntegration() {
      try {
        buttonText.value = locale.generatingUrl;

        const { redirectUrl } = await generate();

        await delaySeconds(1);

        buttonText.value = locale.redirecting;

        // eslint-disable-next-line no-restricted-globals
        location.href = redirectUrl;
      } catch (err) {
        buttonText.value = locale.error;

        await delaySeconds(1);

        buttonText.value = locale.createNew;
      }
    }

    async function run() {
      await fetch();

      buttonText.value = locale.createNew;
    }

    run();

    return {
      featureFlagEnabled,
      locale,
      hasIntegration,
      buttonText,
      isLoading,
      isProcessing,
      createIntegration,
      configurationUrl,
    };
  },
});
</script>
